import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useSearchParamState, enumCodec } from '@/hooks/useSearchParamState';
import { HORIZONS } from '@/lib/coordination/stats';
import { StaffingProgressPanels } from './StaffingProgress';

/**
 * Coordination workbench, Overview tab (part 3e, 18 Sep 2026). The coordinator's pipeline numbers,
 * computed live: fully staffed share, slots filled per role, open slots not yet worked, the
 * 30/60/90/120-day windows, remaining weddings this year and next, and this week's movement.
 * Horizon toggle: next 120 days (her dashboard) or every upcoming event.
 */

async function authedFetch(path: string) {
  const { data: { session } } = await supabaseAdmin.auth.getSession();
  if (!session) throw new Error('Not signed in');
  const res = await fetch(path, { headers: { Authorization: `Bearer ${session.access_token}` } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

const horizonCodec = enumCodec(HORIZONS.map((h: { key: string }) => h.key), '120');
const pctTone = (p: number | null) => (p === null ? 'text-muted-foreground' : p >= 85 ? 'text-emerald-700' : p >= 70 ? 'text-amber-700' : 'text-destructive');
const shortDate = (d?: string | null) => (d ? new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Denver' }) : '');

function Tile({ value, label, sub, tone = '' }: { value: string | number; label: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-lg border bg-card px-3 py-2.5 shadow-sm">
      <div className={`text-2xl font-bold tabular-nums leading-tight ${tone}`}>{value}</div>
      <div className="text-xs text-muted-foreground leading-snug">{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground/80">{sub}</div>}
    </div>
  );
}

function Bar({ pct }: { pct: number | null }) {
  return <div className="h-2 w-full rounded-full bg-muted"><div className={`h-2 rounded-full ${pct !== null && pct >= 85 ? 'bg-emerald-500' : pct !== null && pct >= 70 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${pct ?? 0}%` }} /></div>;
}

export default function OverviewTab() {
  const [horizon, setHorizon] = useSearchParamState('horizon', '120', horizonCodec) as [string, (v: string) => void];
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    authedFetch(`/api/admin/coordination/stats?horizon=${horizon}`).then((d) => !cancelled && setData(d)).catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [horizon]);

  if (error) return <p className="text-sm text-destructive">Could not load the overview: {error}</p>;
  if (!data) return <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Counting…</div>;

  const s = data.summary;
  const ph = data.slots.photographer, vd = data.slots.videographer, sp = data.slots.second_photographer;
  const a = data.activity;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Pipeline <span className="font-normal text-muted-foreground">· {data.horizon_label}{data.horizon_end ? ` (through ${shortDate(data.horizon_end)})` : ''}</span></h2>
        <div className="flex gap-1 rounded-md border bg-muted/40 p-0.5 text-xs" role="tablist" aria-label="Horizon">
          {data.horizons.map((h: any) => (
            <button key={h.key} type="button" role="tab" aria-selected={horizon === h.key} onClick={() => setHorizon(h.key)} className={`rounded px-2.5 py-1 ${horizon === h.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{h.label}</button>
          ))}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Tile value={s.pct === null ? '—' : `${s.pct}%`} label={`of events fully staffed (${s.staffed} of ${s.events})`} tone={pctTone(s.pct)} />
        <Tile value={data.open_slots} label="photographer / videographer slots still unfilled" tone={data.open_slots > 0 ? 'text-destructive' : 'text-emerald-700'} />
        <Tile value={`${ph.filled} / ${ph.needed}`} label="photographer slots filled" sub={ph.needed ? `${Math.round((ph.filled / ph.needed) * 100)}%` : ''} />
        <Tile value={`${vd.filled} / ${vd.needed}`} label="videographer slots filled" sub={vd.needed ? `${Math.round((vd.filled / vd.needed) * 100)}%` : ''} />
        <Tile value={data.not_started.photographer} label="events where photo outreach has not started" tone={data.not_started.photographer > 0 ? 'text-destructive' : ''} />
        <Tile value={data.not_started.videographer} label="events where video outreach has not started" tone={data.not_started.videographer > 0 ? 'text-destructive' : ''} />
        <Tile value={a.assignments} label={`talent locked in the past ${a.days} days`} sub={`${a.assignments_by_role.photographer} photo · ${a.assignments_by_role.videographer} video${a.assignments_by_role.second_photographer ? ` · ${a.assignments_by_role.second_photographer} 2nd` : ''}`} />
        <Tile value={a.new_bookings} label={`new bookings in the past ${a.days} days`} />
      </div>
      {sp.needed > 0 && <p className="text-xs text-muted-foreground">2nd photographer slots: {sp.filled} of {sp.needed} filled.</p>}
      <p className="text-xs text-muted-foreground">
        Also tracking: {a.outreach} talent reached out to in the past {a.days} days · {a.listed_on_indeed} listed on Indeed · {a.awaiting_reply} awaiting reply
        {data.outreach_available === false ? ' (outreach tables not installed)' : ''}
      </p>

      <StaffingProgressPanels data={data} />
    </div>
  );
}
